import { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled, { keyframes, css } from "styled-components";
import { userOrder } from '../../states/userOrder';
import { FaCalendarAlt, FaClock, FaBolt, FaCheckCircle } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const PopUpProgramarNew = props => {
    const { popUpFechaEntrega, setpopUpFechaEntrega, color_botton, btnTextColor } = props;
    const { setDataUser, dataUser } = userOrder();

    const [mode, setMode] = useState("ahora"); // "ahora" | "programar"
    const [dateTime, setDateTime] = useState(new Date());

    const dateRef = useRef(null);
    const timeRef = useRef(null);

    const openDate = () => dateRef.current.setOpen(true);
    const openTime = () => timeRef.current.setOpen(true);

    function formatDateTimeLocal(date) {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const HH = String(date.getHours()).padStart(2, "0");
        const ii = String(date.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${HH}:${ii}`;
    }

    const handleConfirm = () => {
        if (mode === "ahora") {
            setDataUser({ dateDelivery: null });
        } else {
            setDataUser({ dateDelivery: formatDateTimeLocal(dateTime) });
        }
        setpopUpFechaEntrega(false);
    };

    const formatDate = (date) =>
        date.toLocaleDateString("es-CO", { weekday: "short", day: "2-digit", month: "short" });

    const formatTime = (date) =>
        date.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit", hour12: true });

    if (!popUpFechaEntrega) return null;

    return (
        <Overlay onClick={() => setpopUpFechaEntrega(false)}>
            <Sheet onClick={(e) => e.stopPropagation()}>
                {/* Handle y cerrar */}
                <Handle />
                <CloseBtn onClick={() => setpopUpFechaEntrega(false)}>
                    <IoClose size={20} />
                </CloseBtn>

                {/* Header */}
                <Header>
                    <HeaderTitle>¿Cuándo lo entregamos?</HeaderTitle>
                    <HeaderSub>Elige si quieres recibirlo ahora o en otro momento</HeaderSub>
                </Header>

                {/* Toggle ahora / programar */}
                <ToggleRow>
                    <ToggleBtn
                        $active={mode === "ahora"}
                        $color={color_botton}
                        onClick={() => setMode("ahora")}
                    >
                        <FaBolt size={13} />
                        Llevar ahora
                    </ToggleBtn>
                    <ToggleBtn
                        $active={mode === "programar"}
                        $color={color_botton}
                        onClick={() => setMode("programar")}
                    >
                        <FaCalendarAlt size={13} />
                        Programar
                    </ToggleBtn>
                </ToggleRow>

                {/* Contenido según modo */}
                {mode === "ahora" ? (
                    <NowCard>
                        <FaCheckCircle size={36} color={color_botton || "#6C63FF"} />
                        <NowTitle>Entrega lo antes posible</NowTitle>
                        <NowSub>Tu pedido saldrá en cuanto esté listo</NowSub>
                    </NowCard>
                ) : (
                    <>
                        <PickerLabel>Selecciona fecha y hora</PickerLabel>
                        <PickersRow>
                            {/* Fecha */}
                            <PickerCard onClick={openDate} $color={color_botton}>
                                <DatePicker
                                    ref={dateRef}
                                    selected={dateTime}
                                    onChange={(date) => {
                                        const n = new Date(dateTime);
                                        n.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
                                        setDateTime(n);
                                    }}
                                    minDate={new Date()}
                                    dateFormat="yyyy-MM-dd"
                                    customInput={<input style={{ display: "none" }} />}
                                />
                                <PickerIconCircle $color={color_botton}>
                                    <FaCalendarAlt size={18} color={color_botton || "#6C63FF"} />
                                </PickerIconCircle>
                                <PickerValue>
                                    {dateTime.toLocaleDateString("es-CO", { day: "2-digit", month: "short" }).toUpperCase()}
                                </PickerValue>
                                <PickerSubLabel>Fecha</PickerSubLabel>
                            </PickerCard>

                            {/* Hora */}
                            <PickerCard onClick={openTime} $color={color_botton}>
                                <DatePicker
                                    ref={timeRef}
                                    selected={dateTime}
                                    onChange={(time) => {
                                        const n = new Date(dateTime);
                                        n.setHours(time.getHours(), time.getMinutes());
                                        setDateTime(n);
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Hora"
                                    dateFormat="h:mm aa"
                                    customInput={<input style={{ display: "none" }} />}
                                />
                                <PickerIconCircle $color={color_botton}>
                                    <FaClock size={18} color={color_botton || "#6C63FF"} />
                                </PickerIconCircle>
                                <PickerValue>
                                    {formatTime(dateTime)}
                                </PickerValue>
                                <PickerSubLabel>Hora</PickerSubLabel>
                            </PickerCard>
                        </PickersRow>

                        <SummaryPill>
                            📅 {formatDate(dateTime)} · {formatTime(dateTime)}
                        </SummaryPill>
                    </>
                )}

                {/* Botón confirmar */}
                <ConfirmBtn
                    $color={color_botton}
                    $textColor={btnTextColor}
                    onClick={handleConfirm}
                >
                    {mode === "ahora" ? "Confirmar — llevar ahora" : "Confirmar fecha y hora"}
                </ConfirmBtn>
            </Sheet>
        </Overlay>
    );
};

/* ── Animations ─────────────────────────────── */
const slideUp = keyframes`
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

/* ── Styled Components ───────────────────────── */
const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(3px);
    z-index: 1200;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    animation: ${fadeIn} 0.2s ease;
`;

const Sheet = styled.div`
    width: 100%;
    max-width: 540px;
    background: #FFFFFF;
    border-radius: 28px 28px 0 0;
    padding: 12px 20px 32px 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    animation: ${slideUp} 0.3s cubic-bezier(0.34, 1.26, 0.64, 1);
    position: relative;
`;

const Handle = styled.div`
    width: 40px;
    height: 4px;
    background: #D8D8E0;
    border-radius: 99px;
    align-self: center;
    margin-bottom: 4px;
`;

const CloseBtn = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: #F3F2F4;
    border: none;
    border-radius: 99px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #5C5C7A;
    transition: background 0.15s;
    &:hover { background: #E8E8EF; }
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-right: 36px;
`;

const HeaderTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1A1A2E;
    letter-spacing: -0.3px;
`;

const HeaderSub = styled.p`
    margin: 0;
    font-size: 13px;
    color: #8888A0;
    line-height: 1.4;
`;

const ToggleRow = styled.div`
    display: flex;
    gap: 10px;
    background: #F3F2F4;
    border-radius: 16px;
    padding: 5px;
`;

const ToggleBtn = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 44px;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    ${({ $active, $color }) =>
        $active
            ? css`
                background: ${$color || "#6C63FF"};
                color: #FFFFFF;
                box-shadow: 0 3px 12px color-mix(in srgb, ${$color || "#6C63FF"} 40%, transparent);
            `
            : css`
                background: transparent;
                color: #8888A0;
            `}
`;

const NowCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 28px 20px;
    background: #F7F7FA;
    border-radius: 20px;
`;

const NowTitle = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1A1A2E;
`;

const NowSub = styled.p`
    margin: 0;
    font-size: 13px;
    color: #8888A0;
`;

const PickerLabel = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #5C5C7A;
    padding-left: 2px;
`;

const PickersRow = styled.div`
    display: flex;
    gap: 12px;
`;

const PickerCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px 12px 16px 12px;
    background: #F7F7FA;
    border: 2px solid #EBEBF0;
    border-radius: 20px;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
    &:hover, &:active {
        border-color: ${({ $color }) => $color || "#6C63FF"};
        box-shadow: 0 0 0 4px color-mix(in srgb, ${({ $color }) => $color || "#6C63FF"} 12%, transparent);
    }
`;

const PickerIconCircle = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: color-mix(in srgb, ${({ $color }) => $color || "#6C63FF"} 12%, transparent);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PickerValue = styled.p`
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: #1A1A2E;
    text-align: center;
`;

const PickerSubLabel = styled.p`
    margin: 0;
    font-size: 12px;
    color: #AAAAB5;
    font-weight: 500;
`;

const SummaryPill = styled.div`
    align-self: center;
    background: #F3F2F4;
    border-radius: 99px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    color: #1A1A2E;
`;

const ConfirmBtn = styled.button`
    width: 100%;
    height: 54px;
    border: none;
    border-radius: 16px;
    background: ${({ $color }) => $color || "#6C63FF"};
    color: ${({ $textColor }) => $textColor || "#FFFFFF"};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.2px;
    cursor: pointer;
    transition: opacity 0.15s ease, transform 0.12s ease;
    box-shadow: 0 4px 16px color-mix(in srgb, ${({ $color }) => $color || "#6C63FF"} 35%, transparent);
    &:active {
        opacity: 0.88;
        transform: scale(0.98);
    }
`;

export default PopUpProgramarNew;
